import type { Maybe } from '@@types/helpers'
import type { Features } from '@alsid/common'
import { AppRouteName } from '@app/routes'
import type { StoreRoot } from '@app/stores'
import StoreFlags from '@app/stores/helpers/StoreFlags'
import StoreModal from '@app/stores/helpers/StoreModal'
import StoreBase from '@app/stores/StoreBase'
import { handleStoreError } from '@libs/errors/handleStoreError'
import { assertUnreachableCase } from '@libs/productive-codebases/toolbox/src'
import type { IWSRegistration } from '@server/routers/WSProxyRouter/types'
import { WSEntityName } from '@server/routers/WSProxyRouter/types'
import {
  type IWSFeatureFlagMessage,
  type IWSToggleRouterMessage,
  WSFeatureFlagPayloadMessages
} from '@server/routers/WSToggleRouter/types'
import type { IObservableValue } from 'mobx'
import { action, computed, makeObservable, observable } from 'mobx'
import type { IStoreOptions } from '../types'
import type { FeatureFlags } from './types'

export default class StoreFeatureFlags extends StoreBase {
  public storeFlags = new StoreFlags(this.storeRoot)
  public storeModalFeatureFlagUpdate = new StoreModal(this.storeRoot)
  public storeModalFeatureFlagRestart = new StoreModal(this.storeRoot, {
    isUnclosable: true
  })

  private _featureFlagsWSRegistration: Maybe<
    IWSRegistration<IWSFeatureFlagMessage['payload']>
  > = null

  private _analyticsQueues: Set<Features> = new Set()

  private _$timeoutInSeconds: IObservableValue<number>

  // list of enabled feature flags
  private _$enabledFeatureFlags = observable.map<Features, boolean>()

  private _$featureFlagsHaveChanged = observable.box<boolean>(false)

  constructor(storeRoot: StoreRoot, options: IStoreOptions = {}) {
    super(storeRoot, options)

    this._$timeoutInSeconds = observable.box<number>(
      this.storeRoot.environment.config.app.featuretoggler
        .timeoutinsecondsbeforeupdateconsidered
    )

    makeObservable(this)

    // start loop to send LD analytics every X ms
    this._startSendAnalyticsLoop()
  }

  /**
   * Fetch feature flags.
   */
  fetchFeatureFlags(): Promise<void> {
    this.storeFlags.loading()

    const url = this.storeRoot.appRouter.makeRouteInfosPathname({
      routeName: AppRouteName.MiddlewareFeatureFlags,
      parameters: {}
    })

    return this.storeRoot.fetchClient
      .get(url)
      .then(response => {
        if (response.status !== 200) {
          throw new Error('Unable to get the feature flags')
        }

        return response.json() as Promise<FeatureFlags>
      })
      .then(data => {
        this.storeFlags.success()

        if (data) {
          this._setFeatureFlags(data)
        }
      })
      .then(() => this.registerEventWS())
      .catch(handleStoreError(this.storeRoot, this.storeFlags))
  }

  /**
   * Return the list of all the enabled features
   */
  getEnabledFeatures() {
    return Array.from(this._$enabledFeatureFlags.keys())
  }

  /**
   * Return the value of the flag and enqueue an analytic message.
   */
  getFeatureFlagValue(featureFlagName: Features): boolean {
    this._analyticsQueues.add(featureFlagName)

    return this._$enabledFeatureFlags.get(featureFlagName) || false
  }

  /**
   * Perform the matching action when a websocket message is received.
   */
  onWSMessage(payload: IWSFeatureFlagMessage['payload']) {
    switch (payload.message) {
      // When a restart has already been triggered before the app launch
      case WSFeatureFlagPayloadMessages.update_received:
        this.triggerUpdateReceived()
        return

      case WSFeatureFlagPayloadMessages.restarting_soon:
        if (payload.timeoutValue) {
          this.setTimeoutInSeconds(payload.timeoutValue)
        }

        this.triggerUpdateReceived()
        return

      case WSFeatureFlagPayloadMessages.server_up:
        this.restartIfFeatureFlagsHaveChanged()
        return

      case WSFeatureFlagPayloadMessages.restart:
        location.reload()
        return

      default:
        assertUnreachableCase(payload.message)
    }
  }

  /**
   * Show a modal when a FT change is received.
   */
  triggerUpdateReceived() {
    this.setFeatureFlagsHaveChanged(true)

    this.storeModalFeatureFlagUpdate.show()

    setTimeout(() => {
      location.reload()
    }, this.timeoutInSeconds * 1000)
  }

  restartIfFeatureFlagsHaveChanged() {
    if (this._$featureFlagsHaveChanged.get()) {
      location.reload()
    }
  }

  /** Actions */

  /**
   * Set feature flags have changed.
   */
  @action
  setFeatureFlagsHaveChanged(value: boolean): this {
    this._$featureFlagsHaveChanged.set(value)

    return this
  }

  /**
   * Set timeout in seconds.
   */
  @action
  setTimeoutInSeconds(value: number): this {
    this._$timeoutInSeconds.set(value)

    return this
  }

  /**
   * Subscribe to feature flags.
   */
  @action
  registerEventWS() {
    // don't register several times
    if (this._featureFlagsWSRegistration) {
      return
    }

    this._featureFlagsWSRegistration = this.storeRoot.wsClient.addRegistration<
      IWSFeatureFlagMessage['payload']
    >(
      'FeatureFlags',
      {
        name: WSEntityName.featureFlags,
        payload: {}
      },
      this.onWSMessage.bind(this)
    )
  }

  /**
   * Unsubscribe events.
   */
  @action
  unregisterEventWS() {
    if (this._featureFlagsWSRegistration) {
      this.storeRoot.wsClient.removeRegistration(
        this._featureFlagsWSRegistration
      )
      this._featureFlagsWSRegistration = null
    }
  }

  /** Computed */

  /**
   * Return the timeout in seconds.
   */
  @computed
  get timeoutInSeconds(): number {
    return this._$timeoutInSeconds.get()
  }

  /** Private */

  /**
   * Initiates a loop that batches and sends pending feature flag analytics events.
   */
  private _startSendAnalyticsLoop() {
    setTimeout(() => {
      this._sendAnalytics()
      this._startSendAnalyticsLoop()
    }, 5000)
  }

  /**
   * Send analytics event to LD.
   */
  private _sendAnalytics() {
    this._analyticsQueues.forEach(featureToggleName => {
      const message: IWSToggleRouterMessage = {
        command: 'sendFTAnalytics',
        entity: WSEntityName.featureFlags,
        featureToggleName
      }

      this.storeRoot.wsClient.send(message)

      this._analyticsQueues.delete(featureToggleName)
    })
  }

  /**
   * Set feature flags.
   */
  @action
  private _setFeatureFlags(featureFlags: FeatureFlags): this {
    this._$enabledFeatureFlags.clear()

    Object.entries(featureFlags).forEach(
      ([featureFlagName, featureFlagValue]) => {
        this._$enabledFeatureFlags.set(
          featureFlagName as Features,
          featureFlagValue
        )
      }
    )

    return this
  }
}
